import { ClientLoggingService } from '../../../../../shared/client-logging.service';
import { Component, OnInit, inject } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { RbacService } from '../../../../../shared/rbac.service';
import { PermissionRefreshService } from '../../../../../shared/permission-refresh.service';
import { lastValueFrom, take } from 'rxjs';
import { FirebaseAuthService } from '../../../../../shared/firebase-auth.service';
import { UserProfileService } from '../../../../../shared/user-profile.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';

export interface ShareDialogData {
  projectId: string;
}

export interface ProjectMember {
  userId: string;
  email: string;
  /** Display name from Firebase Auth (admin SDK) or current user profile. */
  displayName?: string;
  /** Photo URL from Firebase Auth or current user profile. */
  photoUrl?: string;
  role: number;
  roleDisplayName: string;
}

/** Dialog for managing project sharing: inviting collaborators by email and displaying/revoking existing access. */
@Component({
  selector: 'app-share-dialog',
  templateUrl: './share-dialog.component.html',
  styleUrls: ['./share-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatListModule,
    MatDividerModule,
    MatTooltipModule,
    MatMenuModule,
    MatChipsModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressBarModule,
  ],
})
export class ShareDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ShareDialogComponent>>(MatDialogRef);
  data = inject<ShareDialogData>(MAT_DIALOG_DATA);
  private rbacService = inject(RbacService);
  private permissionRefreshService = inject(PermissionRefreshService);
  private firebaseAuthService = inject(FirebaseAuthService);
  private userProfileService = inject(UserProfileService);

  members: ProjectMember[] = [];
  newMemberEmail: string = '';
  selectedRole: number = 1; // Default to Reader
  emailChip: string | null = null; // Email in chip form
  chipAlreadyInvited = false; // Track if chip user was already invited during validation
  loading = false;
  error: string | null = null;
  emailError: string | null = null; // Email validation error
  isOwner = false;
  currentUserEmail: string | null = null;
  isChipValidating = false;
  permissionsLoaded = false; // Track if permissions have been loaded

  // Available roles for invitation
  availableRoles = [
    { value: 1, name: 'Reader', description: 'Can view project content' },
    { value: 2, name: 'Prompter', description: 'Can use AI prompts' },
    { value: 3, name: 'Editor', description: 'Can edit project content' },
    { value: 4, name: 'Owner', description: 'Full access to project settings and billing' },
  ];

  // Reactive form control for proper Angular Material validation
  emailFormControl = new FormControl(
    { value: '', disabled: true }, // Start disabled, will be enabled in ngOnInit based on permissions
    [Validators.email, this.customEmailValidator.bind(this)]
  );

  async ngOnInit(): Promise<void> {
    // Start loading immediately
    this.loading = true;

    try {
      // Get current user email for comparison
      const user = await lastValueFrom(this.firebaseAuthService.user$.pipe(take(1)));
      this.currentUserEmail = (user as any)?.email || null;

      // Execute both API calls in parallel
      const [userProjects, projectMembers] = await Promise.all([
        this.rbacService.getUserProjects(),
        this.rbacService.getProjectMembers(this.data.projectId),
      ]);

      // Check if current user is project owner using the already fetched data
      const currentProject = userProjects.find(p => p.architecturalProjectId === this.data.projectId);
      this.isOwner = currentProject && currentProject.role === 4; // UserRole.OWNER = 4
      ClientLoggingService.log('INFO', `[ShareDialog] Current user is project owner: ${this.isOwner}`);

      // Mark permissions as loaded
      this.permissionsLoaded = true;

      // Batch-fetch Firestore user profiles for all project members.
      // This provides real Google display names and avatars for every collaborator,
      // populated at their last login (Issue #1406 — Firestore profile cache approach).
      const memberUids = projectMembers.map(m => m.userId).filter(Boolean);
      const firestoreProfiles = await this.userProfileService.getUserProfiles(memberUids);

      // Process project members:
      // Priority: Firestore profile → gRPC backend field → Firebase Auth client profile (current user only)
      this.members = projectMembers.map(member => {
        const firestoreProfile = member.userId ? firestoreProfiles.get(member.userId) : null;

        let displayName: string | undefined =
          firestoreProfile?.displayName?.trim() || member.displayName?.trim() || undefined;
        let photoUrl: string | undefined = firestoreProfile?.photoUrl?.trim() || member.photoUrl?.trim() || undefined;

        // Fallback to client-side Firebase Auth profile for the currently signed-in
        // user in case their Firestore profile has not been written yet.
        if (!displayName && member.email === this.currentUserEmail) {
          displayName = (user as any)?.displayName || undefined;
          photoUrl = (user as any)?.photoURL || undefined;
        }

        return {
          userId: member.userId,
          email: member.email,
          displayName,
          photoUrl,
          role: member.globalRole,
          roleDisplayName: this.rbacService.getUserRoleDisplayName(member.globalRole),
        };
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error loading share dialog data:', error);
      this.error = 'Failed to load project data. Please try again.';
    } finally {
      this.loading = false;
      this.updateEmailFormDisabledState();
    }
  }

  /**
   * Reload project members after operations that modify the list
   */
  private async reloadProjectMembers(): Promise<void> {
    try {
      const [projectMembers, user] = await Promise.all([
        this.rbacService.getProjectMembers(this.data.projectId),
        lastValueFrom(this.firebaseAuthService.user$.pipe(take(1))),
      ]);

      const memberUids = projectMembers.map(m => m.userId).filter(Boolean);
      const firestoreProfiles = await this.userProfileService.getUserProfiles(memberUids);

      this.members = projectMembers.map(member => {
        const firestoreProfile = member.userId ? firestoreProfiles.get(member.userId) : null;
        let displayName: string | undefined =
          firestoreProfile?.displayName?.trim() || member.displayName?.trim() || undefined;
        let photoUrl: string | undefined = firestoreProfile?.photoUrl?.trim() || member.photoUrl?.trim() || undefined;
        if (!displayName && member.email === this.currentUserEmail) {
          displayName = (user as any)?.displayName || undefined;
          photoUrl = (user as any)?.photoURL || undefined;
        }
        return {
          userId: member.userId,
          email: member.email,
          displayName,
          photoUrl,
          role: member.globalRole,
          roleDisplayName: this.rbacService.getUserRoleDisplayName(member.globalRole),
        };
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error reloading project members:', error);
      this.error = 'Failed to reload project members. Please try again.';
    }
  }

  /**
   * Retry loading the dialog data
   */
  async retryLoad(): Promise<void> {
    this.error = null;
    await this.ngOnInit();
  }

  // ---------------------------------------------------------------------------
  // Identity helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the best available display label for a member:
   * Firebase Auth displayName if present, otherwise the email local-part.
   */
  getMemberDisplayName(member: ProjectMember): string {
    return member.displayName || member.email.split('@')[0];
  }

  /**
   * Derives 1–2 uppercase initials from a display name or email local-part.
   * Uses a Unicode-aware letter property escape (/\p{L}/u) so non-ASCII names
   * like "Ömer" or "René" produce the correct leading character.
   */
  getMemberInitials(member: ProjectMember): string {
    const source = (member.displayName || member.email || '').trim();
    if (!source) return '?';

    const nameParts = source
      .replace(/@.*/, '') // strip email domain
      .split(/[\s._-]+/)
      .map(part => part.trim())
      .filter(part => part.length > 0)
      .map(part => part[0])
      .filter(char => /\p{L}/u.test(char));

    if (nameParts.length >= 2) {
      return (nameParts[0] + nameParts[1]).toUpperCase();
    }
    return source.slice(0, 1).toUpperCase();
  }

  // Handle keyboard events on both the input and the chip
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.emailFormControl.value?.trim()) {
        this.createEmailChip();
      }
    } else if ((event.key === 'Backspace' || event.key === 'Delete') && this.emailChip) {
      // If a chip is present, Backspace/Delete should unwrap it.
      event.preventDefault();
      this.removeEmailChip();
    }
  }

  // Creates the chip immediately and then starts async validation.
  createEmailChip(): void {
    // First, mark the control as touched to ensure validation messages can be shown.
    this.emailFormControl.markAsTouched();
    this.emailFormControl.updateValueAndValidity(); // Force re-validation.

    if (this.emailFormControl.invalid) {
      return; // Validation failed, errors will be displayed.
    }

    const email = this.emailFormControl.value?.trim() || '';
    if (!email) {
      return;
    }

    const clientValidationError = this.validateEmailForChip(email);
    if (clientValidationError) {
      this.setServerError('emailExists', clientValidationError);
      return;
    }

    // Immediately create the chip
    this.emailChip = email;
    this.emailFormControl.markAsUntouched();
    this.emailFormControl.markAsPristine();
    this.emailFormControl.setErrors(null);
    this.clearServerErrors();

    // Focus the new chip after it's rendered
    setTimeout(() => {
      const chipInputElement = document.querySelector('.email-field.chip-state input') as HTMLInputElement;
      if (chipInputElement) {
        chipInputElement.focus();
      }
    }, 0);

    // Start async validation in the background
    this.checkChipAuthorization(email);
  }

  async checkChipAuthorization(email: string): Promise<void> {
    this.isChipValidating = true;
    try {
      const isAuthorized = await this.firebaseAuthService.isAllowListed(email);
      if (!isAuthorized) {
        this.setServerError('allowlistError', `User ${email} is not on the allowlist.`);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ShareDialog] Authorization check failed:', error);
      this.setServerError('serverError', 'An error occurred while checking authorization.');
    } finally {
      this.isChipValidating = false;
    }
  }

  removeEmailChip(): void {
    if (this.emailChip) {
      // If we're removing a chip, stop any pending validation for it.
      this.isChipValidating = false;
      this.emailFormControl.setValue(this.emailChip); // Restore for editing
      this.emailChip = null;
      this.clearServerErrors();

      // After unwrapping, focus the input and move cursor to the end
      setTimeout(() => {
        const inputElement = document.querySelector('.email-field input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
          try {
            inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
          } catch (error) {
            ClientLoggingService.log('DEBUG', '[ShareDialog] setSelectionRange not supported:', error);
          }
        }
      }, 0);
    }
  }

  onEmailInput(): void {
    // Clear server errors when user starts typing (but keep validation errors)
    this.clearServerErrors();
  }

  isValidEmail(email: string): boolean {
    // More comprehensive email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  validateEmail(email: string): string | null {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return 'Email is required';
    }

    if (!this.isValidEmail(trimmedEmail)) {
      return 'Please enter a valid email address';
    }

    // Check if email is already a member
    const isExistingMember = this.members.some(member => member.email.toLowerCase() === trimmedEmail.toLowerCase());

    if (isExistingMember) {
      return 'User is already a member of this project';
    }

    return null; // No error
  }

  // Custom validator for business logic only (not email format)
  customEmailValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const email = control.value.trim();

    // Only check if email is already a member (let Angular's email validator handle format)
    const isExistingMember = this.members.some(member => member.email.toLowerCase() === email.toLowerCase());

    if (isExistingMember) {
      return { emailExists: { value: email } };
    }

    return null; // Valid
  }

  // Validate email for chip creation (client-side only)
  validateEmailForChip(email: string): string | null {
    // Check if email is already a member
    const isExistingMember = this.members.some(member => member.email.toLowerCase() === email.toLowerCase());

    if (isExistingMember) {
      return 'User is already a member of this project';
    }

    return null; // Client-side validation passed
  }

  // Set a server-side error on the form control
  setServerError(errorType: string, message: string): void {
    // CRITICAL: Must enable control FIRST, then set errors
    // Setting errors on disabled controls doesn't work properly
    const wasDisabled = this.emailFormControl.disabled;
    if (wasDisabled) {
      this.emailFormControl.enable();
    }

    // If we're in chip state, set FormControl value to chip value for proper error binding
    if (this.emailChip && this.emailFormControl.value !== this.emailChip) {
      this.emailFormControl.setValue(this.emailChip);
    }

    // Now set the error
    const currentErrors = this.emailFormControl.errors || {};
    currentErrors[errorType] = { message };
    this.emailFormControl.setErrors(currentErrors);
    this.emailFormControl.markAsTouched();
  }

  // Clear server-side errors while keeping validation errors
  clearServerErrors(): void {
    if (this.emailFormControl.errors) {
      const errors = { ...this.emailFormControl.errors };
      delete errors['serverError'];
      delete errors['allowlistError'];

      if (Object.keys(errors).length === 0) {
        this.emailFormControl.setErrors(null);
      } else {
        this.emailFormControl.setErrors(errors);
      }

      // After clearing server errors, update disabled state
      // (might be able to disable now if no errors remain)
      this.updateEmailFormDisabledState();
    }
  }

  // Update the disabled state of the email form control
  updateEmailFormDisabledState(): void {
    const shouldBeDisabled = !this.isOwner || this.loading;
    const hasErrors = this.emailFormControl.errors && Object.keys(this.emailFormControl.errors).length > 0;

    // Don't disable the control if it has errors (errors won't display on disabled controls)
    if (shouldBeDisabled && this.emailFormControl.enabled && !hasErrors) {
      this.emailFormControl.disable();
    } else if (!shouldBeDisabled && this.emailFormControl.disabled) {
      this.emailFormControl.enable();
    } else if (hasErrors && this.emailFormControl.disabled) {
      this.emailFormControl.enable();
    }
  }

  hasInvitationReady(): boolean {
    return !!(this.emailChip || (this.emailFormControl.value?.trim() && this.emailFormControl.valid));
  }

  async onShareInvitation(): Promise<void> {
    if (!this.isOwner) {
      this.error = 'Only project owners can invite users';
      return;
    }

    let emailToInvite: string;

    if (this.emailChip) {
      emailToInvite = this.emailChip;
    } else {
      const currentEmail = this.emailFormControl.value?.trim();
      if (!currentEmail) {
        this.emailFormControl.markAsTouched();
        return;
      }
      // If there's text in the input, we should validate and create a chip first.
      // However, for a simpler UX, we'll just use the email directly.
      emailToInvite = currentEmail;
    }

    try {
      this.loading = true;
      const result = await this.rbacService.inviteUserToProject(this.data.projectId, emailToInvite, this.selectedRole);

      if (result.success) {
        await this.reloadProjectMembers();
        this.emailChip = null;
        this.emailFormControl.setValue('');
        this.clearServerErrors();
        // Also reset the role selector to default
        this.selectedRole = 1;
      } else {
        const errorMessage = result.message || 'Failed to invite user.';
        this.setServerError('serverError', errorMessage);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ShareDialog] Invitation failed:', error);
      this.setServerError('serverError', this.getInvitationErrorMessage(error));
    } finally {
      this.loading = false;
    }
  }

  private getInvitationErrorMessage(error: unknown): string {
    if (typeof error === 'string' && error.trim()) {
      return error.trim();
    }

    if (error && typeof error === 'object') {
      const grpcError = error as { message?: unknown; details?: unknown; description?: unknown };
      for (const candidate of [grpcError.message, grpcError.details, grpcError.description]) {
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }
    }

    return 'An unexpected error occurred during invitation.';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onRemoveUser(member: ProjectMember): Promise<void> {
    if (!this.isOwner) {
      this.error = 'Only project owners can remove users';
      return;
    }

    // Prevent owner from removing themselves
    if (member.email === this.currentUserEmail) {
      this.error = 'You cannot remove yourself from the project';
      return;
    }

    try {
      this.loading = true;
      this.updateEmailFormDisabledState(); // Update disabled state when loading starts
      this.error = null;

      const result = await this.rbacService.removeUserFromProject(this.data.projectId, member.email);

      if (result.success) {
        // Notify permission change service
        await this.permissionRefreshService.notifyPermissionChange('remove', this.data.projectId, member.email);
        await this.reloadProjectMembers();
      } else {
        this.error = result.message || 'Failed to remove user';
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error removing user:', error);
      this.error = 'Failed to remove user. Please try again.';
    } finally {
      this.loading = false;
      this.updateEmailFormDisabledState();
    }
  }

  async onChangeRole(member: ProjectMember, newRole: number): Promise<void> {
    if (!this.isOwner) {
      this.error = 'Only project owners can change roles.';
      return;
    }

    try {
      this.loading = true;
      const result = await this.rbacService.updateUserRole(this.data.projectId, member.email, newRole);

      if (result.success) {
        // We don't need to notify the permission refresh service here
        // because changing a role doesn't affect the current user's
        // own permissions or token. A simple UI refresh is enough.
        await this.reloadProjectMembers();
      } else {
        this.error = result.message || 'Failed to update role.';
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ShareDialog] Role update failed:', error);
      this.error = 'An error occurred while updating the role.';
    } finally {
      this.loading = false;
    }
  }

  canManageUser(member: ProjectMember): boolean {
    // Only owners can manage users, and they can't manage themselves
    return this.isOwner && member.email !== this.currentUserEmail;
  }

  getRoleColor(role: number): string {
    switch (role) {
      case 4:
        return 'primary'; // Owner
      case 1:
        return 'accent'; // Reader
      case 2:
        return 'warn'; // Prompter
      case 3:
        return ''; // Editor
      default:
        return '';
    }
  }

  getRoleIcon(role: number): string {
    switch (role) {
      case 1:
        return 'visibility'; // Reader
      case 2:
        return 'psychology'; // Prompter
      case 3:
        return 'edit'; // Editor
      case 4:
        return 'verified_user'; // Owner
      default:
        return 'help';
    }
  }

  onCopyLink(): void {
    const currentUrl = window.location.href;
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        ClientLoggingService.log('INFO', 'Link copied to clipboard');
        // TODO: Show success message
      })
      .catch(err => {
        ClientLoggingService.log('ERROR', 'Failed to copy link:', err);
      });
  }

  onDone(): void {
    this.dialogRef.close();
  }
}
